import { Inject, Injectable } from '@angular/core';
import { BaseDataSourceAbstract } from '@base/datalayer/BaseDataSourceAbstract';
import { combineLatest, forkJoin, of } from 'rxjs';
import { IBaseModuleComponent } from '@base/component/IBaseComponent';
import { APP_CONFIG, AppConfig } from './app.config.model';
import { map, mergeMap } from 'rxjs/operators';
import { Utils } from '@core/helpers/Utils';
import { EnumLanguages } from '@core/framework/sysenums.enum';
import { LoginGuard } from '@m4u-angular/core/framework/login.guard';
import { ConfirmCancelGuard } from '@m4u-angular/core/framework/confirm-cancel.guard';
import { BaseDataResponse } from '../base/entities/BaseEntities';

export interface VersionDescriptor {
    moduleName: string;
    major: number;
    minor: number;
    build: number;
    builds: number[];
}

export interface AppBuildConfig {
    deploymentPort: string;
    deploymentLocation: string;
    deploymentApiRoot: string;
    customBuild: string;
    isRegisterServiceWorker: boolean;
    title: string;
    standaloneApplicationApiUrl: string;
    projects: [{
        [key: string]: {}
    }];
    languages: string[];
    [key: string]: any;
}

@Injectable()
export class AppConfigService {
    public AppConfig: AppConfig;
    public M4UConfig: AppBuildConfig;
    public Resources: any[];
    public CustomResources: any[];

    constructor(@Inject(APP_CONFIG) appConfig) {
        this.AppConfig = appConfig;
        this.M4UConfig = AppConfigService.loadM4UConfig();
        this.Resources = AppConfigService._resources;
        this.CustomResources = AppConfigService._customResources;
    }

    //Get module name by component, endpoint or service instance
    getModuleNameByInstance = (instance): string => {
        //If instance is in application components return its module name
        const componentInstance = this.AppConfig.Components.find(c => instance instanceof c);
        if (componentInstance) return (componentInstance as IBaseModuleComponent).ModuleName;

        //If instance is in application endpoints return its module name
        const endpointInstance = this.AppConfig.Endpoints.find(e => instance instanceof e);
        if (endpointInstance) return (endpointInstance as IBaseModuleComponent).ModuleName;

        //If instance is in application services return its module name
        const serviceInstance = this.AppConfig.Services.find(s => {
            if (typeof (s) === 'object') {
                if (s.hasOwnProperty('useClass')) return instance instanceof s['useClass'];
                if (s.hasOwnProperty('useValue')) return instance instanceof s['useValue'];
                return false;
            }
            return instance instanceof s;
        });
        if (serviceInstance) return (serviceInstance as IBaseModuleComponent).ModuleName;

        return '';
    }

    getModulesVersion = (): VersionDescriptor[] => {
        AppConfigService.loadM4UConfig();
        const result = Object.keys(AppConfigService._m4uConfig.projects).map(projectName => {
            const path = `assets/projects/${projectName}/version.json`;
            let version = Utils.LoadJsonFile<VersionDescriptor>(path);
            if (version) {
                version.moduleName = projectName;
                version.build = version.builds && version.builds.length ? Math.max(...version.builds) : 1;
            } else {
                version = { moduleName: projectName, major: 0, minor: 0, build: 0, builds: [] } as VersionDescriptor;
            }
            return version;
        });
        return result;
    }

    private static _m4uConfig: AppBuildConfig;
    static loadM4UConfig() {
        if (!this._m4uConfig) {
            this._m4uConfig = Utils.LoadJsonFile('menahel4u.json');
            this._m4uConfig.deploymentPort = this.GetDeploymentPort();
            this._m4uConfig.deploymentLocation = this.GetDeploymentLocation();            
        }
        return this._m4uConfig;
    }

    //Return real port in case it's development one
    private static GetDeploymentPort() : string
    {
        return this.IsDevelopmentPort() ? (location.protocol == 'http:' ? '4444' : '44333') : location.port;
    }

    //Check whether this is our special development port
    private static IsDevelopmentPort() : boolean
    {
        return (location.port == '4200' || location.port == '4201');
    }

    
    //We are calling the server using a relative path here, with a special override for development environment to switch the port
    private static GetDeploymentLocationAPIURL() : string
    {
        const sRelativePath = 'api/System/DeploymentLocation';
        if(this.IsDevelopmentPort()) return `http://${location.hostname}:${this.GetDeploymentPort()}/${sRelativePath}`;
        return sRelativePath;
    }

    //Obtain the deployment server-side location (IIS virtual directory name, basically) by calling the server
    private static GetDeploymentLocation() : string
    {
        const oDeploymentLocation: BaseDataResponse<string> = Utils.LoadJsonFile(this.GetDeploymentLocationAPIURL());
        if (oDeploymentLocation != null && oDeploymentLocation.IsSuccess) return oDeploymentLocation.Data; else return '';
    }

    //Application config variable to avoid several times loading
    private static _appConfig: AppConfig;
    static loadConfig(): AppConfig {
        //If already loaded just return
        if (this._appConfig) return this._appConfig;

        const m4uConfig = this.loadM4UConfig();
        //Validation for any project in menahel4u configuration file
        if (!m4uConfig.projects || !m4uConfig.projects) {
            throw new Error("There should be at least one project in menahel4u.json");
        }

        const configs = [];
        //Load application configuration for each project in menahel4u configuration file
        Object.keys(m4uConfig.projects).forEach(projectName => {
            const appConfigModule = require(`projects/${projectName}/_app.config`);
            appConfigModule.appConfig.ProjectName = projectName;
            configs.push(appConfigModule.appConfig);
        });
        this.loadResources(configs);
        //Merge all the projects application configuration files into one
        this._appConfig = this.mergeConfigs(configs);
        return this._appConfig;
    }

    private static getRoutes(_configs: AppConfig[], routeNames: string) {
        return _configs.map(x => x)
            .reduce((arr, el) => {
                const routes = el[routeNames];
                routes.forEach(r => {
                    //Set module name for routes with CommonSetting component and its data
                    if (r.data && r.data.DataSource) {
                        (r.component as any as IBaseModuleComponent).ModuleName = el.ModuleName;
                        r.data.ModuleName = el.ModuleName;
                    }
                    //Get current route component
                    const routeComponent = r.component as any as IBaseModuleComponent;
                    //Get current route component module name
                    const moduleNameWithPrefix = routeComponent && routeComponent.ModuleName
                        ? `${routeComponent.ModuleName}_`
                        : '';
                    //Set route path depends on its component module name
                    r.path = `${moduleNameWithPrefix}${r.path}`;
                });
                return arr.concat(routes);
            }, [])
    }

    //Merge application configuration files into one
    static mergeConfigs(configs: AppConfig[]): AppConfig {
        //Set module name for each of the project components, endpoints and services before application starts
        configs.forEach(x => {
            x.Components.forEach(c => (c as IBaseModuleComponent).ModuleName = x.ModuleName);
            x.Endpoints.forEach(e => (e as IBaseModuleComponent).ModuleName = x.ModuleName);
            x.Services.forEach(s => {
                let service;
                if (typeof (s) === 'object') {
                    if (s.hasOwnProperty('useClass')) service = s['useClass'];
                    if (s.hasOwnProperty('useValue')) service = s['useValue'];
                } else service = s;
                (service as IBaseModuleComponent).ModuleName = x.ModuleName;
            });
        });
        return {
            //Join all the projects module names by ','
            OnGetHomePagePath: function () {
                let routes = [...this.RoutesWithEmptyLayout, ...this.RoutesAnonymous, ...this.RoutesProtected].filter(x => x?.data?.IsHomePage);
                return routes.length > 0 ? routes[0].path : '';
            },
            ModuleName: configs.map(x => x.ModuleName).join(),
            //Join all the projects project names by ','
            ProjectName: configs.map(x => x.ProjectName).join(),
            //Get application mode from the first app config file
            Mode: configs[0].Mode,
            //Get notifications available from the first app config file
            IsAllowNotifications: configs[0].IsAllowNotifications,
            //Get breadcrumb available from the first app config file
            ShowBreadcrumb: configs[0].ShowBreadcrumb,

            //Merge all the projects components into one array
            Components: configs.map(x => x.Components).reduce((arr, el) => arr.concat(el), []),
            //Merge all the projects entry components into one array
            EntryComponents: configs.map(x => x.EntryComponents).reduce((arr, el) => arr.concat(el), []),
            //Merge all the projects endpoints into one array
            Endpoints: configs.map(x => x.Endpoints).reduce((arr, el) => arr.concat(el), []),
            //Merge all the projects services into one array
            Services: configs.map(x => x.Services).reduce((arr, el) => arr.concat(el), []),

            //Merge all the projects routes into one array and add module name prefix for each of the route
            RoutesProtected: this.getRoutes(configs, 'RoutesProtected')
                .map((item) => {
                    let canDeactivateModules = !!item.canDeactivate && item.canDeactivate.length > 0 ? [...item.canDeactivate.filter(d => !!d), ConfirmCancelGuard] : [ConfirmCancelGuard];
                    return { ...item, canActivate: [LoginGuard, ...(item.canActivate || [])], canDeactivate: canDeactivateModules }
                }),
            RoutesAnonymous: this.getRoutes(configs, 'RoutesAnonymous')
                .map((item) => {
                    let canDeactivateModules = !!item.canDeactivate && item.canDeactivate.length > 0 ? [...item.canDeactivate.filter(d => !!d), ConfirmCancelGuard] : [ConfirmCancelGuard];
                    return { ...item, canDeactivate: canDeactivateModules }
                }),
            RoutesWithEmptyLayout: this.getRoutes(configs, 'RoutesWithEmptyLayout'),

            SupportLegacyDisplaySizes: configs.findIndex(x => !!x.SupportLegacyDisplaySizes) !== -1,

            OnInitApplicationAsync: (userInfo?: any) => {
                //Merge all the projects on init application functions into one
                return combineLatest(configs.map(x => x.OnInitApplicationAsync(userInfo)))
                    .pipe(map(x => x.reduce((res, el) => res && el, true)))
            },

            //Merge all the projects on logout application functions into one
            OnLogoutApplicationAsync: () => combineLatest(
                configs.map(x => x.OnLogoutApplicationAsync()))
                .pipe(map(x => x.reduce((res, el) => res && el, true))
                ),

            //Merge all the projects data sources into one array and add module name prefix for each of the datasources
            OnGetGlobalDataSources: () => {
                let dssObservables = configs.map(x => x.OnGetGlobalDataSources());
                return forkJoin(dssObservables)
                    .pipe(mergeMap(dss => {
                        let result = {};
                        dss.forEach((ds: { [id: string]: BaseDataSourceAbstract }, idx) => {
                            Object.keys(ds).forEach(key => result[`${configs[idx].ModuleName}${key}`] = ds[key]);
                        });
                        return of(result);
                    }));
            },

            //Get the default routes params from the first of the application configuration files
            OnGetDefaultRouteParams: configs[0].OnGetDefaultRouteParams,

            //Merge all the projects on get login extension links functions into one
            OnGetAppLoginExtensionLinks: () => {
                let linksObservables = configs.map(x => x.OnGetAppLoginExtensionLinks());
                return forkJoin(linksObservables)
                    .pipe(mergeMap(links => {
                        let result = [];
                        links.forEach((link, idx) => {
                            link.forEach(linkItem => this.setMenuModuleName(linkItem, configs[idx].ModuleName));
                            result = result.concat(link);
                        });
                        return of(result);
                    }));
            },

            //Merge all the projects menus into one array and add module name prefix for each of the menu and its child
            OnGetMenu: (userInfos?: { [moduleName: string]: any }) => {
                let menusObservables = configs.map(x => x.OnGetMenu(userInfos));
                return forkJoin(menusObservables)
                    .pipe(mergeMap(menus => {
                        let result = [];
                        menus.forEach((menu, idx) => {
                            menu.forEach(menuItem => this.setMenuModuleName(menuItem, configs[idx].ModuleName));
                            result = result.concat(menu);
                        });
                        return of(result);
                    }));
            },

            //Merge all the projects navbar items into one array and add module name prefix for each of the navbar
            OnGetNavbarItems: (userInfos?: { [moduleName: string]: any }) => {
                let menusObservables = configs.map(x => x.OnGetNavbarItems(userInfos));
                return forkJoin(menusObservables)
                    .pipe(mergeMap(menus => {
                        let result = [];
                        menus.forEach((menu, idx) => {
                            menu.forEach(menuItem => this.setMenuModuleName(menuItem, configs[idx].ModuleName));
                            result = result.concat(menu);
                        });
                        return of(result);
                    }));
            },

            GetLists: () => {
                let result = [];
                configs.forEach(x => {
                    const lists = x.GetLists();
                    lists.forEach(l => l.Properties.ModuleName = x.ModuleName);
                    result = result.concat(lists);
                });
                return result;
            },

            GetInjectableComponentOptions: () => {
                let observables = configs.map(x => x.GetInjectableComponentOptions());
                return forkJoin(observables).pipe(mergeMap((options) => {
                    let result = [];
                    options.forEach((option) => { result = result.concat(option); })
                    return of(result);
                }));
            },

            //Merge all the projects enums into one object
            Enums: configs.map(x => x.Enums)
                .reduce((obj, el) => Object.assign(obj, el), {}),

            OnLoginPage: () => {
                return combineLatest(configs.map(x => x.OnLoginPage()))
                    .pipe(map(x => x.reduce((res, el) => res && el, true)))
            },

            ShareableEntities: configs.map(x => x.ShareableEntities).flat(1),
        } as AppConfig
    }

    //Set module name for the menu and its child
    static setMenuModuleName(menu, moduleName) {
        menu.moduleName = moduleName;
        menu.menu = moduleName + '.' + menu.menu;
        if (menu.items) menu.items.forEach(subMenu => this.setMenuModuleName(subMenu, moduleName));
    }

    //Merge resources function
    private static _resources: any[];
    private static _customResources: any[];
    static loadResources(configs: AppConfig[]) {
        this._resources = [];
        this._customResources = [];
        const languages = Object.values(EnumLanguages);
        const randomNoCacheParam = Math.random();

        configs.forEach(x => {
            languages.forEach(language => {
                if (this._m4uConfig.customBuild) {
                    let path = `assets/i18n/${x.ProjectName}/${language}_${this._m4uConfig.customBuild}.json`;
                    if (!(window as any).cordova) path += `?v=${randomNoCacheParam}`;
                    const customResource = Utils.LoadJsonFile(path) as any;
                    if (customResource) {
                        customResource.Properties.ModuleName = x.ModuleName;
                        this._customResources.push(customResource);
                    }
                }

                let path = `assets/i18n/${x.ProjectName}/${language}.json`;
                if (!(window as any).cordova) path += `?v=${randomNoCacheParam}`;
                const resource = Utils.LoadJsonFile(path) as any;
                if (resource) {
                    resource.Properties.ModuleName = x.ModuleName;
                    this._resources.push(resource);
                }
            });
        });
    }

    public static GetLoadedLanguages(): string[] {
        let languages: string[]=[];
        for (var i = 0; i <= this._resources.length - 1; i++) {
            languages.push(this._resources[i].Properties.Language)
        }
        return languages;
    }
}